/**
 * Every settings entry, in one place. The settings list (mobile screen and
 * the web side pane) is rendered from this; adding an entry is one line.
 *
 * - `panel`: opens inside the Settings screen (its own component there).
 * - `screen`: opens its own screen; `activeScreens` keeps the entry
 *   highlighted in the side pane while any of them is open.
 * - `admin`: owners and admins only.
 * - `module`: plan module it needs; outside the plan it shows locked with
 *   an Upgrade link (subscriptions without a module list are unrestricted).
 */
import type { Screen } from '../contexts/AppContext';

export type SettingsGroup = 'account' | 'business' | 'team' | 'channels' | 'sales' | 'messaging' | 'advanced';

export type SettingsPanel = 'profile' | 'phone' | 'notifications' | 'developer';

export interface SettingsEntry {
  id: string;
  group: SettingsGroup;
  icon: string;
  panel?: SettingsPanel;
  screen?: Screen;
  activeScreens?: Screen[];
  admin?: boolean;
  module?: string;
}

export const SETTINGS_GROUPS: SettingsGroup[] = ['account', 'business', 'team', 'channels', 'sales', 'messaging', 'advanced'];

export const SETTINGS_ENTRIES: SettingsEntry[] = [
  // Account — everyone
  { id: 'profile', group: 'account', icon: 'person-circle-outline', panel: 'profile' },
  { id: 'phone', group: 'account', icon: 'key-outline', panel: 'phone' },
  { id: 'notifications', group: 'account', icon: 'notifications-outline', panel: 'notifications' },

  // Business
  { id: 'business', group: 'business', icon: 'briefcase-outline', screen: 'business-profile', admin: true },
  { id: 'billing', group: 'business', icon: 'card-outline', screen: 'subscription', activeScreens: ['subscription', 'billing-addons', 'plan-selection'], admin: true },
  { id: 'setup', group: 'business', icon: 'rocket-outline', screen: 'channel-onboarding-stepper', admin: true },

  // Team
  { id: 'team', group: 'team', icon: 'people-outline', screen: 'user-setup', admin: true },
  { id: 'permissions', group: 'team', icon: 'shield-checkmark-outline', screen: 'business-permissions', admin: true },
  { id: 'assignmentRules', group: 'team', icon: 'git-branch-outline', screen: 'assignment-rules', activeScreens: ['assignment-rules', 'assignment-rule-edit'], admin: true },
  { id: 'departments', group: 'team', icon: 'git-network-outline', screen: 'departments', activeScreens: ['departments', 'department-add', 'department-edit'], admin: true, module: 'departments' },

  // Channels
  { id: 'ai', group: 'channels', icon: 'ai-bot', screen: 'ai-setup', admin: true, module: 'ai' },
  { id: 'aiUsage', group: 'advanced', icon: 'bar-chart-outline', screen: 'ai-usage', admin: true },
  { id: 'whatsapp', group: 'channels', icon: 'logo-whatsapp', screen: 'whatsapp-business-setup', admin: true, module: 'whatsapp' },
  { id: 'messenger', group: 'channels', icon: 'logo-facebook', screen: 'facebook-messenger-setup', admin: true, module: 'messenger' },
  { id: 'posts', group: 'channels', icon: 'newspaper-outline', screen: 'posts-list', activeScreens: ['posts-list', 'post-create'], admin: true, module: 'messenger' },
  { id: 'x', group: 'channels', icon: 'logo-twitter', screen: 'x-twitter-setup', admin: true, module: 'x_twitter' },
  { id: 'phoneChannel', group: 'channels', icon: 'call-outline', screen: 'phone-setup', admin: true, module: 'phone' },
  { id: 'visits', group: 'channels', icon: 'walk-outline', screen: 'visits-setup', admin: true, module: 'visits' },

  // Sales & catalogue
  { id: 'taxes', group: 'sales', icon: 'receipt-outline', screen: 'business-config', admin: true, module: 'orders' },
  { id: 'shipping', group: 'sales', icon: 'car-outline', screen: 'shipping-methods', activeScreens: ['shipping-methods', 'shipping-method-create', 'shipping-method-edit'], admin: true, module: 'shipping' },
  { id: 'units', group: 'sales', icon: 'scale-outline', screen: 'units', admin: true, module: 'products' },
  { id: 'inventory', group: 'sales', icon: 'cube-outline', screen: 'inventory', admin: true, module: 'inventory' },
  { id: 'coupons', group: 'sales', icon: 'pricetag-outline', screen: 'coupons', admin: true, module: 'coupons' },
  { id: 'referrals', group: 'sales', icon: 'gift-outline', screen: 'referrals', admin: true, module: 'referrals' },
  // All business documents (quotation, invoice, receipt, delivery note, packing slip, purchase order…).
  { id: 'quotationTemplates', group: 'sales', icon: 'documents-outline', screen: 'quotation-templates', activeScreens: ['quotation-templates', 'quotation-template-edit'], admin: true, module: 'orders' },

  // Messaging
  { id: 'templates', group: 'messaging', icon: 'chatbox-ellipses-outline', screen: 'template-management', admin: true, module: 'templates' },
  { id: 'quickReplies', group: 'messaging', icon: 'flash-outline', screen: 'quick-replies', activeScreens: ['quick-replies', 'quick-reply-items', 'quick-reply-set-detail', 'quick-reply-set-create', 'quick-reply-set-edit', 'quick-reply-set-items', 'quick-reply-item-create', 'quick-reply-item-edit'], admin: true, module: 'quick_replies' },

  // Advanced
  { id: 'devices', group: 'advanced', icon: 'hardware-chip-outline', screen: 'devices', activeScreens: ['devices', 'device-create', 'device-edit'], admin: true },
  { id: 'developer', group: 'advanced', icon: 'code-slash-outline', panel: 'developer', admin: true, module: 'api' },
];

export const settingsEntry = (id: string) => SETTINGS_ENTRIES.find((e) => e.id === id);

/** The entry to highlight while `screen` is open (side pane on web). */
export const settingsEntryForScreen = (screen: Screen): SettingsEntry | undefined =>
  SETTINGS_ENTRIES.find((e) => e.screen === screen || e.activeScreens?.includes(screen));
